import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Toast,
  NavBar,
  Radio,
  Space,
  ImageUploader,
  Dialog,
} from 'antd-mobile';
import { ImageUploadItem } from 'antd-mobile/es/components/image-uploader';
import { brandService } from '../services/brandService';
import { uploadService } from '../services/uploadService';
import './BrandEdit.css';

const BrandEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<ImageUploadItem[]>([]);

  const isEditMode = id && id !== 'new';

  useEffect(() => {
    if (isEditMode) {
      loadBrand();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadBrand = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const brand = await brandService.getBrandById(id);
      form.setFieldsValue({
        brand_name: brand.brand_name,
        brand_code: brand.brand_code,
        status: brand.status,
      });
      if (brand.brand_logo) {
        setFileList([{ url: brand.brand_logo }]);
      }
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: '加载品牌失败',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const brandData = {
        ...values,
        brand_logo: fileList[0]?.url || '',
      };

      if (isEditMode && id) {
        await brandService.updateBrand(id, brandData);
        Toast.show({
          icon: 'success',
          content: '更新成功',
        });
      } else {
        await brandService.createBrand(brandData);
        Toast.show({
          icon: 'success',
          content: '创建成功',
        });
      }
      navigate('/brands');
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: isEditMode ? '更新失败' : '创建失败',
      });
      console.error('Error saving brand:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    const result = await Dialog.confirm({
      content: '确定要删除这个品牌吗？',
    });

    if (result) {
      try {
        setLoading(true);
        await brandService.deleteBrand(id);
        Toast.show({
          icon: 'success',
          content: '删除成功',
        });
        navigate('/brands');
      } catch (error) {
        Toast.show({
          icon: 'fail',
          content: '删除失败',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpload = async (file: File) => {
    try {
      Toast.show({
        icon: 'loading',
        content: '上传中...',
        duration: 0,
      });
      
      // 上传到 Supabase Storage
      const url = await uploadService.uploadImage(file, 'brand-logos');
      
      Toast.clear();
      
      if (url.includes('placeholder')) {
        Toast.show({
          icon: 'success',
          content: '使用占位图片（Storage 未配置）',
          duration: 2000,
        });
      } else {
        Toast.show({
          icon: 'success',
          content: '上传成功',
          duration: 1000,
        });
      }
      
      return { url };
    } catch (error) {
      Toast.clear();
      console.error('Upload failed:', error);
      Toast.show({
        icon: 'fail',
        content: '上传失败，使用占位图片',
        duration: 2000,
      });
      // 返回占位图片
      return {
        url: `https://via.placeholder.com/150x150/4A90E2/ffffff?text=${encodeURIComponent(file.name.substring(0, 10))}`,
      };
    }
  };

  return (
    <div className="brand-edit-container">
      <NavBar onBack={() => navigate('/brands')}>
        {isEditMode ? '编辑品牌' : '新建品牌'}
      </NavBar>

      <div className="form-container">
        <Form
          form={form}
          layout="horizontal"
          initialValues={{
            status: 'active',
          }}
        >
          <Form.Item
            name="brand_name"
            label="品牌名称"
            rules={[{ required: true, message: '请输入品牌名称' }]}
          >
            <Input placeholder="请输入品牌名称" />
          </Form.Item>

          <Form.Item
            name="brand_code"
            label="品牌编码"
            rules={[{ required: true, message: '请输入品牌编码' }]}
          >
            <Input placeholder="请输入品牌编码" />
          </Form.Item>

          <Form.Item name="status" label="状态">
            <Radio.Group>
              <Space direction="horizontal">
                <Radio value="active">启用</Radio>
                <Radio value="inactive">停用</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="品牌Logo">
            <ImageUploader
              value={fileList}
              onChange={setFileList}
              upload={handleUpload}
              maxCount={1}
            />
          </Form.Item>
        </Form>
      </div>

      <div className="form-footer">
        <Button
          block
          type="submit"
          color="primary"
          size="large"
          loading={loading}
          onClick={handleSubmit}
        >
          {isEditMode ? '保存' : '创建'}
        </Button>
        {isEditMode && (
          <Button
            block
            color="danger"
            size="large"
            style={{ marginTop: '12px' }}
            onClick={handleDelete}
          >
            删除品牌
          </Button>
        )}
      </div>
    </div>
  );
};

export default BrandEdit;
